import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import DocumentIllustration from './DocumentIllustration';
import type { ArchiveManifest } from './ArchiveContentsSection';

interface SelectedArchiveEntry {
  path: string;
  fileName: string;
  url: string;
  blob: Blob;
  mimeType: string;
}

interface ArchivePreviewProps {
  archiveManifest?: ArchiveManifest | null;
  selectedEntry: SelectedArchiveEntry | null;
  entryLoading: boolean;
  entryError: { code: string; message: string } | null;
  /** Called when the user wants to download the whole archive (fallback). */
  onDownloadArchive?: () => void;
}

const PREVIEW_HEIGHT = 600;

function formatSize(bytes?: number): string {
  if (!bytes || bytes <= 0) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB'];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < u.length - 1) {
    v /= 1024;
    i++;
  }
  return `${Math.round(v * 10) / 10} ${u[i]}`;
}

/** Renders a fetched archive entry blob inline. Handles common MIME types
 *  natively; falls back to a download link for unsupported binary types. */
const ArchiveEntryViewer: React.FC<{ entry: SelectedArchiveEntry }> = ({ entry }) => {
  const { url, mimeType, fileName } = entry;

  if (mimeType.startsWith('image/')) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: PREVIEW_HEIGHT,
          overflow: 'auto',
          backgroundColor: 'background.default',
        }}
      >
        <Box
          component="img"
          src={url}
          alt={fileName}
          sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
        />
      </Box>
    );
  }
  if (mimeType === 'application/pdf') {
    return (
      <Box
        component="iframe"
        src={url}
        title={fileName}
        sx={{ width: '100%', height: PREVIEW_HEIGHT, border: 'none' }}
      />
    );
  }
  if (mimeType.startsWith('video/')) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: PREVIEW_HEIGHT,
          backgroundColor: 'black',
        }}
      >
        <Box
          component="video"
          controls
          src={url}
          sx={{ maxWidth: '100%', maxHeight: '100%' }}
        />
      </Box>
    );
  }
  if (mimeType.startsWith('audio/')) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: PREVIEW_HEIGHT,
          gap: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">{fileName}</Typography>
        <Box component="audio" controls src={url} sx={{ width: '60%', minWidth: 300 }} />
      </Box>
    );
  }
  if (
    mimeType.startsWith('text/') ||
    mimeType === 'application/json' ||
    mimeType === 'application/xml' ||
    mimeType === 'application/javascript' ||
    mimeType === 'application/x-yaml'
  ) {
    return <ArchiveTextEntryViewer entry={entry} />;
  }

  // Fallback: offer download of the entry itself via the object URL.
  return (
    <Box sx={{ p: 3 }}>
      <Alert
        severity="info"
        action={
          <Button
            component="a"
            href={url}
            download={fileName}
            size="small"
            startIcon={<DownloadIcon />}
          >
            Download entry
          </Button>
        }
      >
        Preview not available for {fileName} ({mimeType || 'unknown type'}).
      </Alert>
    </Box>
  );
};

/** Fetches the blob's text content and renders it in a <pre> block. */
const ArchiveTextEntryViewer: React.FC<{ entry: SelectedArchiveEntry }> = ({ entry }) => {
  const [text, setText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setText(null);
    setError(null);
    entry.blob.text()
      .then((t) => { if (!cancelled) setText(t); })
      .catch((e) => { if (!cancelled) setError(e?.message || 'Failed to read entry as text'); });
    return () => { cancelled = true; };
  }, [entry.blob]);

  if (error) {
    return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
  }
  if (text === null) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: PREVIEW_HEIGHT }}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 2,
        height: PREVIEW_HEIGHT,
        overflow: 'auto',
        backgroundColor: 'background.default',
        fontFamily: 'monospace',
        fontSize: '0.85rem',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
    >
      {text}
    </Box>
  );
};

/**
 * Preview slot replacement that's shown for archive content.
 *
 * Three primary states:
 *   1. No entry selected (empty) — show an illustration + summary + hint.
 *   2. Entry loading — illustration + spinner + entry name.
 *   3. Entry loaded — render via ArchiveEntryViewer.
 * Plus an error state with a download-archive fallback.
 */
export const ArchivePreview: React.FC<ArchivePreviewProps> = ({
  archiveManifest,
  selectedEntry,
  entryLoading,
  entryError,
  onDownloadArchive,
}) => {
  const totalEntries =
    archiveManifest?.total_entries ?? archiveManifest?.totalEntries ?? archiveManifest?.entries?.length ?? 0;
  const totalSize =
    archiveManifest?.total_uncompressed_size ?? archiveManifest?.totalUncompressedSize ?? 0;
  const format = (archiveManifest?.format ?? 'archive').toUpperCase();
  const summaryLine = `${format} · ${totalEntries} ${totalEntries === 1 ? 'entry' : 'entries'} · ${formatSize(totalSize)}`;

  // Error overrides the loaded-entry view.
  if (entryError) {
    const friendly = friendlyMessageForCode(entryError.code, entryError.message);
    return (
      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <Box sx={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          height: PREVIEW_HEIGHT, gap: 2, p: 3, textAlign: 'center',
        }}>
          <DocumentIllustration fileType="archive" />
          <Alert severity="warning" sx={{ width: '100%', maxWidth: 480 }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>{friendly}</Typography>
            <Typography variant="caption" color="text.secondary">Error code: {entryError.code}</Typography>
          </Alert>
          {onDownloadArchive && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={onDownloadArchive}
            >
              Download archive
            </Button>
          )}
        </Box>
      </Paper>
    );
  }

  // Loading.
  if (entryLoading) {
    return (
      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <Box sx={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          height: PREVIEW_HEIGHT, gap: 2,
        }}>
          <DocumentIllustration fileType="archive" />
          <CircularProgress size={28} />
          <Typography variant="body2" color="text.secondary">
            Loading {selectedEntry?.fileName ?? 'entry'}...
          </Typography>
        </Box>
      </Paper>
    );
  }

  // Entry loaded.
  if (selectedEntry) {
    return (
      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <Box sx={{
          px: 2, py: 1.25,
          borderBottom: 1, borderColor: 'divider',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
        }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" noWrap title={selectedEntry.path}>
              {selectedEntry.fileName}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap title={selectedEntry.path}>
              {selectedEntry.path} · {formatSize(selectedEntry.blob.size)}
            </Typography>
          </Box>
          <Button
            component="a"
            href={selectedEntry.url}
            download={selectedEntry.fileName}
            size="small"
            startIcon={<DownloadIcon />}
          >
            Download
          </Button>
        </Box>
        <ArchiveEntryViewer entry={selectedEntry} />
      </Paper>
    );
  }

  // Empty state.
  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      <Box sx={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        height: PREVIEW_HEIGHT, gap: 2, p: 3, textAlign: 'center',
      }}>
        <DocumentIllustration fileType="archive" />
        <Typography variant="body2" color="text.secondary">{summaryLine}</Typography>
        <Typography variant="body2">
          Click a file in the Archive Contents below to preview it here.
        </Typography>
      </Box>
    </Paper>
  );
};

function friendlyMessageForCode(code: string, fallback: string): string {
  switch (code) {
    case 'ARCHIVE_ENTRY_NOT_FOUND':
      return 'That entry no longer exists in this archive.';
    case 'ARCHIVE_ENTRY_TOO_LARGE':
      return 'This entry is too large to preview inline. Download the archive to view it.';
    case 'ARCHIVE_PREVIEW_UNSUPPORTED_FORMAT':
      return 'Inline preview is only supported for ZIP archives.';
    case 'NOT_AN_ARCHIVE':
      return 'This content isn’t recognized as an archive.';
    case 'ARCHIVE_EXTRACTION_FAILED':
      return 'Couldn’t extract this entry. The archive may be damaged.';
    case 'CONTENT_NOT_FOUND':
      return 'The archive itself is missing or has been removed.';
    case 'NETWORK_ERROR':
      return 'Couldn’t reach the server. Check your connection and try again.';
    default:
      return fallback || 'Failed to load entry.';
  }
}

export default ArchivePreview;
